import * as XLSX from 'xlsx';

const safe = (v) => (v ?? '');

const safeDate = (v) => {
  if (!v) return '';
  if (typeof v === 'object' && '_seconds' in v) return new Date(v._seconds * 1000).toLocaleDateString('en-US');
  if (typeof v.toDate === 'function') return v.toDate().toLocaleDateString('en-US');
  if (v instanceof Date) return v.toLocaleDateString('en-US');
  return String(v);
};

const wbColWidths = (ws, widths) => {
  ws['!cols'] = widths.map((w) => ({ wch: w }));
};

const downloadXlsx = (workbook, filename) => {
  XLSX.writeFile(workbook, filename);
};

const parseDate = (v) => {
  if (!v) return null;
  if (typeof v === 'object' && '_seconds' in v) return new Date(v._seconds * 1000);
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const yearDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatPeriodLabel = (period) => {
  if (!period) return '';
  const sd = parseDate(period.startDate);
  const ed = parseDate(period.endDate);
  if (!sd || !ed) return `${safeDate(period.startDate)} – ${safeDate(period.endDate)}`;
  if (sd.getFullYear() === ed.getFullYear() && sd.getMonth() === ed.getMonth()) {
    return `${shortDate(sd)}–${ed.getDate()}, ${sd.getFullYear()}`;
  }
  if (sd.getFullYear() === ed.getFullYear()) {
    return `${shortDate(sd)} – ${shortDate(ed)}, ${sd.getFullYear()}`;
  }
  return `${yearDate(sd)} – ${yearDate(ed)}`;
};

export const exportPayPeriodSummary = (entries, period) => {
  const periodLabel = formatPeriodLabel(period);
  const data = entries.map((e) => {
    const hours = e.hours || 0;
    const rate = e.rate || 0;
    const amountOwed = e.amountOwed != null ? e.amountOwed : hours * rate;
    return {
      'Coach Name': e.coachName || '',
      'Job Title': e.jobTitle || '',
      'Employment Type': e.employmentType === 'volunteer' ? 'Volunteer' : 'Paid Coach',
      'Location': e.location || '',
      'Pay Period': periodLabel,
      'Total Hours': hours,
      'Hourly Rate': rate,
      'Amount Owed': amountOwed,
      'Payment Status': e.status === 'paid' ? 'Paid' : e.status === 'approved' ? 'Approved' : (e.status === 'submitted' || e.status === 'ready for payroll' ? 'Ready for Payroll' : e.status || ''),
      'Pay Date': safeDate(e.paymentDate),
      'Notes': '',
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  wbColWidths(ws, [20, 18, 16, 14, 22, 12, 12, 14, 18, 14, 20]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pay Period Summary');
  const filename = `payroll-summary-${periodLabel.replace(/[^a-zA-Z0-9]/g, '-')}.xlsx`;
  downloadXlsx(wb, filename);
};

export const exportCoachHistory = (entries, coachName) => {
  const data = entries.map((e) => ({
    'Pay Period': e.payPeriodId || '',
    'Date': safeDate(e.date),
    'Hours': e.hours || 0,
    'Rate': e.rate || 0,
    'Amount': e.amountOwed || 0,
    'Status': e.status === 'paid' ? 'Paid' : e.status === 'approved' ? 'Approved' : (e.status || ''),
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  wbColWidths(ws, [24, 14, 10, 10, 12, 14]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Coach History');
  const name = (coachName || 'coach').replace(/[^a-zA-Z0-9]/g, '_');
  downloadXlsx(wb, `payroll-history-${name}.xlsx`);
};

export const exportByLocation = (locations) => {
  const data = locations.map((loc) => ({
    'Location': loc.location || 'Unknown',
    'Staff Count': loc.staffCount || 0,
    'Total Hours': loc.totalHours || 0,
    'Total Amount': loc.totalAmount || 0,
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  wbColWidths(ws, [20, 14, 14, 16]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'By Location');
  downloadXlsx(wb, 'payroll-by-location.xlsx');
};

export const exportVolunteerHours = (volunteers) => {
  const data = volunteers.map((v) => ({
    'Coach Name': v.coachName || '',
    'Location': v.location || '',
    'Total Hours': v.hours || 0,
    'Notes': '',
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  wbColWidths(ws, [20, 16, 14, 24]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Volunteer Hours');
  downloadXlsx(wb, 'volunteer-hours.xlsx');
};
